import React, { useState, useCallback, useEffect } from 'react';
import { Box, List, ListItem, ListItemText, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel, Grid, IconButton, CircularProgress, Alert } from '@mui/material';
import ActionButton from '../../components/ActionButton';
import './CalendarSidebar.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

const fetchBranchAvailability = async (branchId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/branch/availability/${branchId}`);
    return response.data.availability;
  } catch (error) {
    console.error('Error fetching branch availability:', error);
    return null;
  }
};

const CalendarSidebar = ({
  entities,
  selectedEntity,
  setSelectedEntity,
  isMobile,
  closeMenu,
  globalAvailability,
  updateEntities,
  loading,
  error,
  handleEntitySelect
}) => {
const userId = localStorage.getItem('userId');
  console.log('Entities received in CalendarSidebar:', entities);

  // Function to safely extract value from potential nested object
  const safeExtract = (obj, key) => {
    if (obj && typeof obj === 'object') {
      if (obj[key] && typeof obj[key] === 'object' && 'S' in obj[key]) {
        return obj[key].S;
      }
      return obj[key];
    }
    return undefined;
  };

  // Ensure entities is always an array of properly formatted objects
const safeEntities = React.useMemo(() => {
    let processedEntities = [];
    if (Array.isArray(entities)) {
      processedEntities = entities;
    } else if (entities && typeof entities === 'object' && entities.data && Array.isArray(entities.data)) {
      processedEntities = entities.data;
    }

    return processedEntities.map(entity => ({
      entityName: safeExtract(entity, 'entityName') || 'Unnamed Entity',
      departmentName: safeExtract(entity, 'departmentName') || '',
      uniqueId: safeExtract(entity, 'uniqueId') || safeExtract(entity, 'entityId') || JSON.stringify(entity),
      branchId: safeExtract(entity, 'branchId') || '',
      // Add other necessary fields here
    }));
  }, [entities]);

  console.log('Safe entities:', safeEntities);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityType, setNewEntityType] = useState('');
  const [newEntityAvailability, setNewEntityAvailability] = useState({});
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  // The sidebar width is now handled in CSS

  useEffect(() => {
    if (!loading && entities.length === 0) {
      console.log('No entities found');
    }
  }, [loading, entities]);

const handleOpenDialog = async (entity = null, userId) => {
  setIsLoadingAvailability(true);
  setErrorMessage('');

  try {
    let branchAvailability = null;
    let fetchedEntityData = null;

    if (entity) {
      console.log('Opening dialog for entity:', entity);
      setEditingEntity(entity);
      
      // Fetch branch availability first
      branchAvailability = await fetchBranchAvailability(entity.branchId);
      console.log('Fetched branch availability:', JSON.stringify(branchAvailability, null, 2));

      // Then fetch entity info
      const entityUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entityinfo`;
      const entityResponse = await axios.get(entityUrl, {
        params: {
          userId: userId,
          uniqueId: entity.uniqueId
        }
      });
      fetchedEntityData = entityResponse.data;
      console.log('Fetched entity data:', JSON.stringify(fetchedEntityData, null, 2));
      
      if (fetchedEntityData) {
        const entityName = fetchedEntityData.entityName || '';
        const entityType = fetchedEntityData.departmentName || '';
        setNewEntityName(entityName);
        setNewEntityType(entityType);
      }
    } else {
      setEditingEntity(null);
      setNewEntityName('');
      setNewEntityType('');
    }

    const updatedAvailability = {};

    if (branchAvailability && typeof branchAvailability === 'object') {
      console.log('Processing branch availability');
      let availabilityData = branchAvailability;
      
      if (branchAvailability.availability) {
        availabilityData = branchAvailability.availability;
      }
      
      if (availabilityData.M) {
        availabilityData = availabilityData.M;
      }

      Object.entries(availabilityData).forEach(([weekday, data]) => {
        console.log(`Processing ${weekday}:`, JSON.stringify(data, null, 2));
        const lowercaseWeekday = weekday.toLowerCase();
        let startTime, endTime, available;

        if (data.M) {
          startTime = data.M.startTime && data.M.startTime.S;
          endTime = data.M.endTime && data.M.endTime.S;
          available = data.M.available && data.M.available.BOOL;
        } else {
          startTime = data.startTime && data.startTime.S;
          endTime = data.endTime && data.endTime.S;
          available = data.available && data.available.BOOL;
        }
        
        if (startTime && endTime) {
          updatedAvailability[lowercaseWeekday] = {
            available: false, // Set to false by default
            branchStartTime: new Date(`1970-01-01T${startTime}`),
            branchEndTime: new Date(`1970-01-01T${endTime}`),
            slots: [],
            editable: true
          };
        }
      });
    }

    console.log('Processed branch availability:', JSON.stringify(updatedAvailability, null, 2));

    if (Object.keys(updatedAvailability).length === 0) {
      console.error('No valid branch availability data found');
      setErrorMessage('No valid branch availability data found. Please set up branch availability first.');
    } else {
      setErrorMessage(''); // Clear the error message if we have valid data
    }

    if (fetchedEntityData && fetchedEntityData.availability) {
      Object.entries(fetchedEntityData.availability).forEach(([weekday, data]) => {
        const lowercaseWeekday = weekday.toLowerCase();
        if (updatedAvailability[lowercaseWeekday]) {
          updatedAvailability[lowercaseWeekday].available = Array.isArray(data.L) && data.L.length > 0;
          updatedAvailability[lowercaseWeekday].slots = Array.isArray(data.L) ? data.L.map(slot => ({
            startTime: slot.M.startTime.S ? new Date(`1970-01-01T${slot.M.startTime.S}`) : null,
            endTime: slot.M.endTime.S ? new Date(`1970-01-01T${slot.M.endTime.S}`) : null
          })) : [];
        }
      });
    }

    console.log('Final entity availability:', JSON.stringify(updatedAvailability, null, 2));
    setNewEntityAvailability(updatedAvailability);
  } catch (error) {
    console.error('Error fetching data:', error);
    setErrorMessage('Failed to fetch data. Please try again.');
  }

  setIsLoadingAvailability(false);
  setOpenDialog(true);
};

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntity(null);
    setNewEntityName('');
    setNewEntityType('');
    setNewEntityAvailability({
      monday: { available: false, startTime: null, endTime: null },
      tuesday: { available: false, startTime: null, endTime: null },
      wednesday: { available: false, startTime: null, endTime: null },
      thursday: { available: false, startTime: null, endTime: null },
      friday: { available: false, startTime: null, endTime: null },
      saturday: { available: false, startTime: null, endTime: null },
      sunday: { available: false, startTime: null, endTime: null },
    });
  };

const [errorMessage, setErrorMessage] = useState('');
const [warningMessage, setWarningMessage] = useState('');
const [showConfirmDialog, setShowConfirmDialog] = useState(false);
const [showErrorDialog, setShowErrorDialog] = useState(false);
const [showOverrideConfirmDialog, setShowOverrideConfirmDialog] = useState(false);
const [isOverride, setIsOverride] = useState(false);
const [overrideDetails, setOverrideDetails] = useState([]);
const navigate = useNavigate();

const [showExistingEntityDialog, setShowExistingEntityDialog] = useState(false);
const [entityDataToSave, setEntityDataToSave] = useState(null);

const [nameError, setNameError] = useState('');
const [typeError, setTypeError] = useState('');
const [availabilityError, setAvailabilityError] = useState('');

const validateForm = () => {
  let isValid = true;

  if (!newEntityName.trim()) {
    setNameError('Entity name is required');
    isValid = false;
  } else {
    setNameError('');
  }

  if (!newEntityType.trim()) {
    setTypeError('Entity type is required');
    isValid = false;
  } else {
    setTypeError('');
  }

  const availableWeekdays = Object.entries(newEntityAvailability).filter(([, day]) => day.available);
  if (availableWeekdays.length === 0) {
    setAvailabilityError('At least one weekday must be selected');
    isValid = false;
  } else {
    const incompleteDays = availableWeekdays.filter(([, day]) => {
      return !day.slots || day.slots.some(slot => !slot.startTime || !slot.endTime);
    });
    if (incompleteDays.length > 0) {
      setAvailabilityError(`Please set both start and end time for all slots on ${incompleteDays.map(([day]) => day).join(', ')}`);
      isValid = false;
    } else {
      setAvailabilityError('');
    }
  }

  return isValid;
};

const handleSaveEntity = async () => {
  setErrorMessage('');
  setWarningMessage('');
  setOverrideDetails([]);
  
  if (!validateForm()) {
    return;
  }

  const availableWeekdays = Object.entries(newEntityAvailability)
    .filter(([, { available }]) => available)
    .map(([weekday]) => weekday);

  if (availableWeekdays.length === 0) {
    setErrorMessage('At least one weekday must be selected');
    setShowErrorDialog(true);
    return;
  }

  let hasOverride = false;
  let hasEmptySlots = false;
  let emptySlotWeekdays = [];
  let newOverrideDetails = [];
  const timeSlots = Object.entries(newEntityAvailability)
    .filter(([, { available }]) => available)
    .map(([weekday, dayData]) => {
      const validSlots = dayData.slots.filter(slot => {
        if (isSlotEmpty(slot)) {
          hasEmptySlots = true;
          emptySlotWeekdays.push(weekday);
          return false;
        }

        if (isSlotOutsideBranchHours(weekday, slot.startTime, slot.endTime)) {
          hasOverride = true;
          newOverrideDetails.push({
            weekday,
            slot: `${slot.startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${slot.endTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
            branchHours: `${dayData.branchStartTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${dayData.branchEndTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
          });
        }

        return true;
      });

      return {
        weekday,
        timeSlots: validSlots.map(slot => ({
          startTime: slot.startTime instanceof Date ? slot.startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}) : slot.startTime,
          endTime: slot.endTime instanceof Date ? slot.endTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}) : slot.endTime
        }))
      };
    });

  if (hasEmptySlots) {
    setErrorMessage(`Please set both start and end time for all slots on ${emptySlotWeekdays.join(', ')}.`);
    setShowErrorDialog(true);
    return;
  }

  if (timeSlots.length === 0) {
    setErrorMessage('No valid time slots. Please set valid start and end times for at least one day.');
    setShowErrorDialog(true);
    return;
  }

  const entityData = {
    uniqueId: editingEntity ? editingEntity.uniqueId : null,
    entityName: newEntityName.trim(),
    entityType: newEntityType.trim(),
    availability: timeSlots
  };

  if (editingEntity) {
    entityData.uniqueId = editingEntity.uniqueId;
  }

  console.log('Prepared entity data:', JSON.stringify(entityData, null, 2));
  console.log('Has override:', hasOverride);

  if (hasOverride) {
    setIsOverride(true);
    setOverrideDetails(newOverrideDetails);
    setShowOverrideConfirmDialog(true);
  } else {
    await saveEntityData(entityData);
  }
};

const saveEntityData = async (entityData) => {
  try {
    let response;
    if (editingEntity) {
      console.log('Updating existing entity:', editingEntity.uniqueId);
      response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/updateEntityAvailability`, entityData);
    } else {
      console.log('Creating new entity');
      response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/postnewentity`, entityData);
    }
    console.log('Server response:', JSON.stringify(response.data, null, 2));
    handleCloseDialog();
    setShowOverrideConfirmDialog(false);
    await updateEntities(); // Ensure the updated list of entities is fetched after saving
  } catch (error) {
    console.error('Error saving entity:', error);
    console.error('Error response:', JSON.stringify(error.response?.data, null, 2));
    setEntityDataToSave(entityData);
    if (error.response && error.response.data && error.response.data.error === 'Entity with this name already exists for the user.') {
      setErrorMessage('Entity with this name already exists for the user.');
      setShowErrorDialog(true);
    } else {
      setErrorMessage(error.response?.data?.error || 'An error occurred while saving the entity');
      setShowErrorDialog(true);
    }
  }
};

const handleCloseErrorDialog = () => {
  setShowErrorDialog(false);
  setErrorMessage('');
};

const renderErrorDialog = () => (
  <Dialog open={showErrorDialog} onClose={handleCloseErrorDialog}>
    <DialogTitle>Error</DialogTitle>
    <DialogContent>
      <Typography>{errorMessage}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCloseErrorDialog}>Close</Button>
      <Button onClick={handleMakeChanges} variant="contained" color="primary">
        Make Changes
      </Button>
    </DialogActions>
  </Dialog>
);

const handleMakeChanges = () => {
  setShowErrorDialog(false);
};

const handleContinueSaveEntity = async () => {
  try {
    // Ensure entityDataToSave has the correct structure
    const dataToSend = {
      ...entityDataToSave,
      weekdays: Array.isArray(entityDataToSave.weekdays) ? entityDataToSave.weekdays : [entityDataToSave.weekdays],
      availability: entityDataToSave.availability || {}
    };
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/postnewentity`, dataToSend);
    handleCloseDialog();
    updateEntities(); // Fetch updated list of entities
  } catch (error) {
    console.error('Error saving entity:', error);
    setErrorMessage(error.response?.data?.error || 'An error occurred while saving the entity');
  } finally {
    setShowConfirmDialog(false);
  }
};

const renderExistingEntityDialog = () => (
  <Dialog open={showExistingEntityDialog} onClose={() => setShowExistingEntityDialog(false)}>
    <DialogTitle>Entity Already Exists</DialogTitle>
    <DialogContent>
      <Typography>
        An entity with this name already exists. Do you want to make changes to the existing entity?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setShowExistingEntityDialog(false)}>Cancel</Button>
      <Button onClick={handleMakeChanges} variant="contained">Make Changes</Button>
    </DialogActions>
  </Dialog>
);


// Add this to the renderEntityDialog function, just before the DialogActions
{errorMessage && (
  <Typography color="error" sx={{ mt: 2 }}>
    {errorMessage}
  </Typography>
)}

const [entityToDelete, setEntityToDelete] = useState(null);
const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

const handleDeleteEntity = async (entityToDelete) => {
  setEntityToDelete(entityToDelete);
  setShowDeleteConfirmDialog(true);
};

const confirmDeleteEntity = async () => {
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/entity`;
    console.log('Deleting entity with URL:', url);
console.log('Sending data to server:', {
      userId: userId,
      branchId: entityToDelete.branchId,
      uniqueId: entityToDelete.uniqueId
    });
await axios.delete(url, {
      data: {
        userId: userId,
        branchId: entityToDelete.branchId,
        uniqueId: entityToDelete.uniqueId
      }
    });
    console.log('Entity deleted successfully');
    updateEntities(); // Fetch updated list of entities
    if (selectedEntity && selectedEntity.uniqueId === entityToDelete.uniqueId) {
      setSelectedEntity(null);
    }
    setShowDeleteConfirmDialog(false);
  } catch (error) {
    console.error('Error deleting entity:', error);
    setErrorMessage(error.response?.data?.error || 'An error occurred while deleting the entity');
  }
};

  const handleNewEntityAvailabilityChange = useCallback((day, field, value, index = 0) => {
    setNewEntityAvailability(prev => {
      const updatedDay = { ...prev[day] };
      if (field === 'available') {
        updatedDay.available = value;
        if (value && (!updatedDay.slots || updatedDay.slots.length === 0)) {
          updatedDay.slots = [];
        }
      } else {
        if (!updatedDay.slots) {
          updatedDay.slots = [];
        }
        if (!updatedDay.slots[index]) {
          updatedDay.slots[index] = {};
        }
        
        updatedDay.slots[index] = {
          ...updatedDay.slots[index],
          [field]: value
        };
      }
      
      // Check if the number of slots exceeds the limit
      if (updatedDay.limit !== undefined && updatedDay.slots.length > updatedDay.limit) {
        console.warn(`Number of slots (${updatedDay.slots.length}) exceeds the limit (${updatedDay.limit})`);
        updatedDay.slots = updatedDay.slots.slice(0, updatedDay.limit);
      }
      
      return { ...prev, [day]: updatedDay };
    });
  }, []);

  const handleAddTimeSlot = useCallback((day) => {
    setNewEntityAvailability(prev => {
      const updatedDay = { ...prev[day] };
      if (!updatedDay.slots) {
        updatedDay.slots = [];
      }
      updatedDay.slots.push({ startTime: null, endTime: null });
      return { ...prev, [day]: updatedDay };
    });
  }, []);

  // Helper function to check if a slot is empty (both start and end times are null)
  const isSlotEmpty = useCallback((slot) => {
    return slot.startTime === null && slot.endTime === null;
  }, []);

  const isSlotOutsideBranchHours = useCallback((day, startTime, endTime) => {
    const dayData = newEntityAvailability[day];
    if (!dayData || !dayData.branchStartTime || !dayData.branchEndTime) {
      return false;
    }
    const slotStart = startTime instanceof Date ? startTime : new Date(`1970-01-01T${startTime}`);
    const slotEnd = endTime instanceof Date ? endTime : new Date(`1970-01-01T${endTime}`);
    const branchStart = dayData.branchStartTime;
    const branchEnd = dayData.branchEndTime;

    // Convert all times to minutes since midnight for easier comparison
    const slotStartMinutes = slotStart.getHours() * 60 + slotStart.getMinutes();
    const slotEndMinutes = slotEnd.getHours() * 60 + slotEnd.getMinutes();
    const branchStartMinutes = branchStart.getHours() * 60 + branchStart.getMinutes();
    const branchEndMinutes = branchEnd.getHours() * 60 + branchEnd.getMinutes();

    return slotStartMinutes < branchStartMinutes || slotEndMinutes > branchEndMinutes;
  }, [newEntityAvailability]);

  const handleRemoveTimeSlot = useCallback((day, index) => {
    setNewEntityAvailability(prev => {
      const updatedDay = { ...prev[day] };
      updatedDay.slots.splice(index, 1);
      return { ...prev, [day]: updatedDay };
    });
  }, []);

  const renderEntityDialog = () => (
    <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>{editingEntity ? 'Edit Entity' : 'Add New Entity'}</DialogTitle>
      <DialogContent>
        {isOverride && (
          <Typography color="error" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>
            Warning: Some time slots are outside the branch availability. Please review carefully before saving.
          </Typography>
        )}
        {warningMessage && (
          <Typography color="warning" sx={{ mt: 2 }}>
            {warningMessage}
          </Typography>
        )}
<TextField
          autoFocus
          margin="dense"
          label="Entity Name"
          fullWidth
          value={newEntityName}
          InputProps={{ readOnly: true }}
          error={!!nameError}
          helperText={nameError}
        />
<TextField
          margin="dense"
          label="Department Name"
          fullWidth
          value={newEntityType}
          InputProps={{ readOnly: true }}
          error={!!typeError}
          helperText={typeError}
        />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Entity Availability
        </Typography>
        {isLoadingAvailability ? (
          <CircularProgress />
        ) : Object.keys(newEntityAvailability).length === 0 ? (
          <Typography>No availability data found. Please contact support.</Typography>
        ) : (
          <Grid container spacing={2}>
            {Object.entries(newEntityAvailability).map(([day, dayData]) => (
              <Grid item xs={12} key={day}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dayData.available}
                        onChange={(e) => handleNewEntityAvailabilityChange(day, 'available', e.target.checked)}
                      />
                    }
                    label={
                      <>
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                        {dayData.branchStartTime && dayData.branchEndTime && (
                          <span> (Branch: {dayData.branchStartTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {dayData.branchEndTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})})</span>
                        )}
                        {dayData.limit !== undefined && (
                          <span> (Limit: {dayData.limit})</span>
                        )}
                      </>
                    }
                  />
                  {dayData.available && (
                    <>
                      {Array.isArray(dayData.slots) && dayData.slots.length > 0 ? (
                        dayData.slots.map((slot, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', ml: 4, mt: 1 }}>
                            <TimePicker
                              value={slot.startTime}
                              onChange={(newValue) => handleNewEntityAvailabilityChange(day, 'startTime', newValue, index)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    width: 110,
                                    '& .MuiInputBase-root': {
                                      backgroundColor: isSlotOutsideBranchHours(day, slot.startTime, slot.endTime) ? '#FFCCCB' : 'inherit',
                                    },
                                  }}
                                />
                              )}
                            />
                            <Typography sx={{ mx: 1 }}>-</Typography>
                            <TimePicker
                              value={slot.endTime}
                              onChange={(newValue) => handleNewEntityAvailabilityChange(day, 'endTime', newValue, index)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    width: 110,
                                    '& .MuiInputBase-root': {
                                      backgroundColor: isSlotOutsideBranchHours(day, slot.startTime, slot.endTime) ? '#FFCCCB' : 'inherit',
                                    },
                                  }}
                                />
                              )}
                            />
                            {index > 0 && (
                              <IconButton 
                                onClick={() => handleRemoveTimeSlot(day, index)} 
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))
                      ) : null}
                      <Button
                        onClick={() => handleAddTimeSlot(day)}
                        startIcon={<AddIcon />}
                        sx={{ ml: 4, mt: 1, alignSelf: 'flex-start' }}
                        disabled={
                          (dayData.limit !== undefined && dayData.slots.length >= dayData.limit) ||
                          dayData.slots.some(isSlotEmpty)
                        }
                      >
                        Add Availability
                      </Button>
                      {dayData.limit !== undefined && dayData.slots.length >= dayData.limit && (
                        <Typography color="error" sx={{ ml: 4, mt: 1 }}>
                          Weekday limit reached
                        </Typography>
                      )}
                      {dayData.slots.some(isSlotEmpty) && (
                        <Typography sx={{ ml: 4, mt: 1, color: 'text.primary' }}>
                          Please fill in the empty time slot before adding a new one
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        {availabilityError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {availabilityError}
          </Typography>
        )}
        {errorMessage && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {editingEntity && (
          <Button
            onClick={() => handleDeleteEntity(editingEntity)}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        )}
        <Box>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveEntity} 
            variant="contained"
            color={isOverride ? "warning" : "primary"}
          >
            {isOverride ? "Review & Save" : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );

  const renderConfirmDialog = () => (
    <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
      <DialogTitle>Entity Already Exists</DialogTitle>
      <DialogContent>
        <Typography>
          An entity with this name already exists. Do you want to continue creating a new entity with the same name?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
        <Button onClick={handleContinueSaveEntity} variant="contained">Continue</Button>
      </DialogActions>
    </Dialog>
  );

  const renderOverrideConfirmDialog = () => (
    <Dialog open={showOverrideConfirmDialog} onClose={() => setShowOverrideConfirmDialog(false)}>
      <DialogTitle>Confirm Save with Overrides</DialogTitle>
      <DialogContent>
        <Typography>
          The following time slots are outside the branch availability:
        </Typography>
        <ul>
          {overrideDetails.map((detail, index) => (
            <li key={index}>{`${detail.weekday}: ${detail.slot} (Branch hours: ${detail.branchHours})`}</li>
          ))}
        </ul>
        <Typography>
          Are you sure you want to save these overriding time slots?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowOverrideConfirmDialog(false)}>Cancel</Button>
        <Button onClick={() => {
          setShowOverrideConfirmDialog(false);
          // The edit form is already open, so we just need to close this dialog
        }} variant="contained" color="primary">
          Review & Adjust
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteConfirmDialog = () => (
    <Dialog open={showDeleteConfirmDialog} onClose={() => setShowDeleteConfirmDialog(false)}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete this entity? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDeleteConfirmDialog(false)}>Cancel</Button>
        <Button onClick={confirmDeleteEntity} variant="contained" color="error">Delete</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box className={isMobile ? 'calendar-sidebar-mobile' : 'calendar-sidebar'}>
      <Box sx={{pl:2}}>
{/* <ActionButton
  text="Add New Entity"
  onClick={() => handleOpenDialog(null, userId)}
  startIcon={<AddIcon />}
  fullWidth
  className="add-entity-button"
/> */}
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error" sx={{ pt: 2 }}>{error}</Typography>
        ) : entities.length === 0 ? (
          <Typography sx={{ pt: 2 }}>No entities found. Add a new entity to get started.</Typography>
        ) : (
          <Box sx={{ 
            height: 'calc(100vh - 64px)', 
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.2)',
              borderRadius: '3px',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0,0,0,.2) transparent'
          }}>
            <List dense disablePadding>
            {safeEntities.map((entity) => {
              const isSelected = selectedEntity && selectedEntity.uniqueId === entity.uniqueId;
              
              return (
                <ListItem
                  key={entity.uniqueId}
                  selected={isSelected}
                  onClick={() => {
                    handleEntitySelect(entity);
                    if (isMobile) closeMenu();
                  }}
                  className={`entity-list-item ${isSelected ? 'selected' : ''}`}
                >
                  <ListItemText
                    primary={entity.entityName}
                    secondary={entity.departmentName}
                    primaryTypographyProps={{ 
                      variant: 'subtitle1',
                      className: 'entity-name'
                    }}
                    secondaryTypographyProps={{ 
                      variant: 'body2',
                      className: 'entity-type'
                    }}
                  />
                  <Box>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDialog(entity, userId);
                    }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
            </List>
          </Box>
        )}
      </Box>
      {renderEntityDialog()}
      {renderConfirmDialog()}
      {renderOverrideConfirmDialog()}
      {renderDeleteConfirmDialog()}
      {renderExistingEntityDialog()}
      {renderErrorDialog()}
    </Box>
  );
};

export default CalendarSidebar;
